import React, { FC, useState, useEffect, useRef } from 'react'
import { View, useWindowDimensions, FlatList, RefreshControl } from 'react-native'
import { useRoute, useIsFocused } from '@react-navigation/native';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

// common components
import CommonHeader from '../../../shared/commonComponents/commonHeader';
import { appColors } from '../../../themes/appTheme';
import { config } from '../../../shared/environment';
import { viewDiscountStyle as styles } from '../../../themes/components/discounts/viewDiscountStyle';
import { getDiscountBasedUserDetails, getPosDiscountHistoryLogs } from '../../../redux/slices/viewDiscountSlice';
import { discountHistoryLogType, discountLogsType, routeType, payloadType } from './viewDiscountModule';
import { ZenText, ZenLoader, ZenEmptyComponent, ZenIcon } from 'zen-core-elements';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';
import useBackHandler from '../../../shared/hooks/useBackHandler';

const PAGE_LIMIT = 10
//Label shown for the discount module this screen was opened from
const discountModuleLabel: Record<string, string> = {
    Manual: 'Manual Discount',
    Automatic: 'Automatic Discount',
    BuyXgetY: 'Buy X Get Y',
    PayXGetFree: 'Pay X Get Free',
}

//Initials shown in the avatar of a log entry
const getInitials = (name?: string) => (name || '?').trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?'

const ViewDiscount: FC = () => {
    const discountHistory = useAppSelector(state => state.viewDiscount?.userDetailsRes?.value?.discountHistory)
    useBackHandler()
    // Variable used to store route data from useRoute hook
    const route: routeType = useRoute()
    // Variable used to store navigation from useNavigation hook
    const navigation = useAppNavigation()
    const { width, height } = useWindowDimensions()
    const isLandscape = width > height
    // This hook returns 'true' if the screen is focused, 'false' otherwise
    const isFocused = useIsFocused()
    // Variable which is used to handle loader
    const [load, setLoad] = useState<boolean>(false)
    // Variable which is used to store discount logs
    const [discountLogs, setDiscountLogs] = useState<discountLogsType[]>([])
    // Total number of log entries reported by the api
    const [logCount, setLogCount] = useState<number>(0)
    // State to store the payload that needs to be sent to the backend to fetch discount data
    const [payload, setPayload] = useState<payloadType>({ offset: 0, limit: PAGE_LIMIT });
    //Variable which is used to handle footerloader
    const [footerLoader, setFooterLoader] = useState<boolean>(false)
    // Variable to find pull to refresh
    const [refreshing, setRefreshing] = useState<boolean>(false);
    // redux dispatch
    const dispatch = useAppDispatch()
    const onMomentumScrollBegin = useRef(false)

    // UseEffect hook is called when page is focused
    useEffect(() => {
        if (isFocused) {
            setLoad(true)
            loadDiscount()
        }
    }, [isFocused])

    // Method which is used to get the discount details and the first page of its logs
    const loadDiscount = () => {
        const firstPage = { offset: 0, limit: PAGE_LIMIT }
        setPayload(firstPage)
        dispatch(getDiscountBasedUserDetails({ storeId: config?.storeId, id: route?.params?.id })).unwrap().then((res) => {
            if (res && res?.discountHistory) {
                getPosDiscountHistoryLog(firstPage);
            } else {
                stopLoaders()
            }
        }).catch((err) => {
            console.log('Error in get discount details', err)
            stopLoaders()
        })
    }

    const stopLoaders = () => {
        setLoad(false);
        setFooterLoader(false);
        setRefreshing(false);
    }

    // Method which is used to get discount history logs
    const getPosDiscountHistoryLog = (currentPayload: payloadType) => {
        dispatch(getPosDiscountHistoryLogs({ storeId: config?.storeId, id: route?.params?.id, data: currentPayload })).unwrap().then((response) => {
            if (response && response?.discountHistoryLog) {
                const newLogs: discountLogsType[] = [];
                let total = 0
                response?.discountHistoryLog?.forEach((item: discountHistoryLogType) => {
                    if (item?.rows) {
                        newLogs.push(...item.rows);
                    }
                    total += Number(item?.count) || 0
                });
                setLogCount(total)
                setDiscountLogs(prev => currentPayload.offset > 0 ? [...prev, ...newLogs] : newLogs)
            }
        }).catch((Error) => {
            console.log('Error in get discount history', Error)
        }).finally(() => {
            stopLoaders()
        })
    }

    const createdBy = [discountHistory?.userDetails?.firstName, discountHistory?.userDetails?.lastName].filter(Boolean).join(' ')
    const discountName = discountHistory?.discount?.promoCode || discountHistory?.discount?.title || 'Discount'
    const moduleLabel = discountModuleLabel[route?.params?.routeName]

    const renderItem = ({ item, index }: { item: discountLogsType, index: number }) => (
        <View style={[styles.logItem, index === discountLogs.length - 1 && { borderBottomWidth: 0 }]}>
            <View style={styles.avatar}>
                <ZenText content={getInitials(item?.userName)} style={styles.avatarText} />
            </View>
            <View style={styles.logBody}>
                <View style={styles.logTopRow}>
                    <ZenText content={item?.userName || 'Unknown user'} style={styles.logUser} numberOfLines={1} />
                    <ZenText content={moment(item?.createdAt).format('MMM DD, YYYY · hh:mm A')} style={styles.logDate} />
                </View>
                <ZenText
                    content={item?.comments || 'No comments added'}
                    style={[styles.logComment, !item?.comments && styles.logCommentEmpty]}
                    numberOfLines={3}
                />
            </View>
        </View>
    )

    const summaryCard = (
        <View style={styles.card}>
            <View style={styles.summaryHeader}>
                <View style={styles.discountIcon}>
                    <ZenIcon type="material-community" name="tag-outline" size={24} color={appColors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                    <ZenText content={discountName} style={styles.discountName} numberOfLines={2} />
                    <View style={styles.chipRow}>
                        {moduleLabel ? <View style={styles.chip}><ZenText content={moduleLabel} style={styles.chipText} /></View> : null}
                        {discountHistory?.discount?.discountType?.discountName ?
                            <View style={[styles.chip, { backgroundColor: appColors.tealPrimary }]}>
                                <ZenText content={discountHistory.discount.discountType.discountName} style={styles.chipText} />
                            </View> : null}
                    </View>
                </View>
            </View>

            <View style={styles.statsRow}>
                <View style={styles.statTile}>
                    <ZenIcon type="material-community" name="chart-line" size={18} color={appColors.primary} />
                    <ZenText content="Times used" style={styles.statLabel} />
                    <ZenText content={String(discountHistory?.discount?.usageCount ?? 0)} style={styles.statValue} />
                </View>
                <View style={styles.statTile}>
                    <ZenIcon type="material-community" name="account-lock-outline" size={18} color={appColors.primary} />
                    <ZenText content="Limit per customer" style={styles.statLabel} />
                    <ZenText content={discountHistory?.discount?.userBasedMaxUse ? String(discountHistory.discount.userBasedMaxUse) : 'No limit'} style={styles.statValue} />
                </View>
            </View>

            <View style={styles.createdBy}>
                <ZenIcon type="material-community" name="account-circle-outline" size={20} color={appColors.labelColor} />
                <ZenText content="Created by" style={styles.statLabel} />
                <ZenText content={createdBy || 'N/A'} style={[styles.logUser, { textAlign: 'right' }]} numberOfLines={1} />
            </View>
        </View>
    )

    const activityCard = (
        <View style={[styles.card, { flex: 1, paddingBottom: 0 }]}>
            <View style={styles.logHeader}>
                <View style={{ flex: 1 }}>
                    <ZenText content="Activity Log" style={styles.sectionTitle} />
                    <ZenText content="Every change made to this discount" style={styles.sectionSubTitle} />
                </View>
                {logCount ?
                    <View style={styles.countBadge}>
                        <ZenText content={`${logCount} ${logCount === 1 ? 'entry' : 'entries'}`} style={styles.chipText} />
                    </View> : null}
            </View>
            <FlatList
                data={discountLogs}
                renderItem={renderItem}
                keyExtractor={(item, index) => index.toString()}
                onMomentumScrollBegin={() => { onMomentumScrollBegin.current = true; }}
                onEndReached={() => {
                    if (!onMomentumScrollBegin.current) return;
                    if (footerLoader) return;
                    if (discountLogs.length >= payload.offset + payload.limit) {
                        setFooterLoader(true);
                        const newPayload = { ...payload, offset: payload.offset + payload.limit };
                        setPayload(newPayload);
                        getPosDiscountHistoryLog(newPayload);
                    }
                }}
                showsVerticalScrollIndicator={false}
                onEndReachedThreshold={0.5}
                refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={() => {
                        setRefreshing(true);
                        loadDiscount();
                    }} />
                }
                ListFooterComponent={footerLoader ?
                    <View style={styles.footerLoader}>
                        <ZenLoader size={"small"} color={appColors?.primary} />
                    </View> : null
                }
                ListEmptyComponent={<ZenEmptyComponent text="No activity recorded yet" viewStyle={{ flex: 1, backgroundColor: 'transparent', justifyContent: 'center', alignItems: 'center' }} />}
                contentContainerStyle={{
                    paddingBottom: 16,
                    flexGrow: discountLogs.length ? undefined : 1
                }}
            />
        </View>
    )

    return (
        <View style={styles.container}>
            <CommonHeader headerName='Discount History' backIcon={true} backIconOnpress={() => { navigation.goBack() }} />
            {load ?
                <View style={styles.loader}>
                    <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                </View>
                :
                <View style={[styles.content, { flexDirection: isLandscape ? 'row' : 'column' }]}>
                    {/* Tablets show the summary beside the activity log, phones above it */}
                    <View style={[styles.column, isLandscape && { width: '38%' }]}>
                        {summaryCard}
                    </View>
                    {activityCard}
                </View>
            }
        </View>
    )
}

export default ViewDiscount;
